import { useCallback, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';

/**
 * Filter state kept in the URL instead of component state.
 *
 * Why this exists: a plain `useState` filter dies with the component. Filter a list, open a row,
 * press Back — the page remounts with empty state and every filter you set is gone, so you set them
 * all again. This is the same argument `useUrlTab` already makes for tabs (see that file), applied to
 * the rest of a list page's controls.
 *
 * Putting the value in the query string makes the browser's own history entry carry it, so Back and
 * Forward restore the exact view the user left — and a filtered list becomes shareable and
 * bookmarkable for free.
 *
 * Three rules make this behave:
 *  • Changing a filter REPLACES the history entry rather than pushing one, so Back leaves the page in
 *    one press instead of walking backwards through every filter the user tried.
 *  • A value equal to its default is DELETED from the query string, so the default view's address
 *    stays clean and "is anything filtered?" stays readable in the URL.
 *  • Writes are COALESCED through one buffer (see the note below), so two filters changed in the same
 *    tick — `setStatus(v); setPage(1)` — both survive instead of the second clobbering the first.
 *
 * Each hook is a drop-in for `useState` — same tuple, and the setter takes a value or an updater
 * function — so converting a page is a one-line change per filter:
 *
 *   const [status, setStatus] = useState<string>('ALL');      // before
 *   const [status, setStatus] = useUrlString('status', 'ALL'); // after
 *
 * Only put FILTER state here — things that describe what the user is looking at. Transient UI state
 * (an open modal, the row being edited, an in-flight export) does not belong in the URL: it would
 * survive a refresh and reopen dialogs nobody asked for.
 */

/*
 * Why these updates are buffered rather than handed straight to `setSearchParams`.
 *
 * React Router's functional form is not a reducer: `setSearchParams(prev => …)` calls back with the
 * params captured during the LAST RENDER, not the freshest ones. So the everyday pair
 *
 *     onChange={(v) => { setStatus(v); setPage(1); }}
 *
 * has both calls start from the same snapshot, and the second `navigate()` silently overwrites the
 * first — the status filter never reaches the URL. That is not a hypothetical: it is exactly what the
 * first version of this hook did.
 *
 * So writes accumulate in one buffer seeded from the live `window.location.search`, and flush once on
 * a microtask — after the whole event handler has run. Every setter in that handler survives, and a
 * "clear all filters" button costs one history entry instead of a dozen.
 */
type ParamPatch = (p: URLSearchParams) => void;

let pending: URLSearchParams | null = null;
let pendingPath: string | null = null;
let flushWith: ((p: URLSearchParams) => void) | null = null;
let queued = false;

function patchParams(patch: ParamPatch, commit: (p: URLSearchParams) => void) {
  if (pending == null) {
    pending = new URLSearchParams(window.location.search);
    pendingPath = window.location.pathname;
  }
  patch(pending);
  flushWith = commit;

  if (pendingPath != null && pending != null && !queued) {
    queued = true;
    queueMicrotask(() => {
      const next = pending;
      const path = pendingPath;
      const run = flushWith;
      pending = null;
      pendingPath = null;
      flushWith = null;
      queued = false;
      // A handler that changes a filter and then navigates away must not have its filter write land
      // on the page it moved to.
      if (next && run && path === window.location.pathname) run(next);
    });
  }
}

/**
 * The same buffer, for URL state that does NOT go through `useUrlValue`.
 *
 * `useUrlParam` / `useUrlTab` (see useUrlTab.ts) wrote with a plain `setSearchParams` built from the
 * render's snapshot, so a handler that touches one of those AND a filter loses one of the two writes.
 * That is not hypothetical: the By-bank "Accounts reconciled" tile clears the open register and sets
 * its own narrowing in one handler — `setBehindOnly(true); setSelectedBank(undefined)` — and the
 * buffered write landed last, so `?bank=NONE` survived and the tile narrowed the account table while
 * leaving somebody else's register open underneath it. Routing both kinds of write through one buffer
 * is the fix, in the one place that already knows how.
 *
 * `useUrlParam` now does. `useUrlTab` has not been converted — nothing changes a tab and a filter in
 * one handler today — but it has the same flaw, so use this if that ever becomes untrue.
 */
export function patchSearchParams(patch: ParamPatch, commit: (p: URLSearchParams) => void) {
  patchParams(patch, commit);
}

interface Codec<T> {
  /** Read the query-string value. Return `undefined` to fall back (junk / stale URL). */
  parse: (raw: string) => T | undefined;
  /** Write the value. Return `null` to drop the param entirely. */
  format: (v: T) => string | null;
  /** Is this value the default? Defaults are never written to the URL. */
  isDefault: (v: T, fallback: T) => boolean;
}

/** Shared core: one query param <-> one piece of typed state. */
function useUrlValue<T>(key: string, fallback: T, codec: Codec<T>): [T, Dispatch<SetStateAction<T>>] {
  const [params, setParams] = useSearchParams();

  const raw = params.get(key);
  const parsed = raw == null ? undefined : codec.parse(raw);
  const value = parsed === undefined ? fallback : parsed;

  // The setter must stay referentially stable (pages pass it to memoised children and effect deps),
  // so the pieces it needs are read through refs rather than closed over as deps.
  const ref = useRef({ value, fallback, codec, key });
  ref.current = { value, fallback, codec, key };

  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      const { fallback: fb, codec: c, key: k } = ref.current;
      patchParams(
        (buf) => {
          // Read the value back out of the buffer, so `setX(prev => …)` called twice in one handler —
          // or after another filter already wrote — still sees the freshest value, not the render's.
          const rawNow = buf.get(k);
          const parsedNow = rawNow == null ? undefined : c.parse(rawNow);
          const current = parsedNow === undefined ? fb : parsedNow;

          const resolved = typeof next === 'function' ? (next as (p: T) => T)(current) : next;
          const s = c.isDefault(resolved, fb) ? null : c.format(resolved);
          if (s == null || s === '') buf.delete(k);
          else buf.set(k, s);
        },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams],
  );

  return [value, set];
}

const eq = <T,>(a: T, b: T) => a === b;

/**
 * Text, select and enum filters. Defaults to a plain `string`; pass the type argument for an
 * optional or enum filter — `useUrlString<Status | undefined>('status', undefined)`.
 *
 * `NoInfer` on the fallback keeps `useUrlString('q', '')` from narrowing the state to the literal
 * type `''`, which would reject every later `setQ('anything')`.
 *
 * The fallback is taken as a REST argument, not a defaulted parameter, because a JS default fires on
 * an explicitly passed `undefined` too: `useUrlString<string | undefined>('retailer', undefined)` —
 * the shape ~50 optional filters are written in — silently became `''`. An empty string is a VALUE,
 * not "nothing selected", so every one of those Selects rendered as a selected option that matched
 * nothing: a blank box where its placeholder ("Retailer", "Raised by") should be. Only an omitted
 * argument falls back to `''` now; an explicit `undefined` stays `undefined`.
 */
export function useUrlString<T extends string | undefined = string>(
  key: string,
  ...rest: [] | [NoInfer<T>]
): [T, Dispatch<SetStateAction<T>>] {
  const fallback = (rest.length ? rest[0] : ('' as NoInfer<T>)) as T;
  return useUrlValue<T>(key, fallback as T, {
    parse: (raw) => raw as T,
    format: (v) => (v == null ? null : String(v)),
    isDefault: eq,
  });
}

/** Checkbox / switch filters. `?key=1` when on; absent when off. */
export function useUrlBool(key: string, fallback = false): [boolean, Dispatch<SetStateAction<boolean>>] {
  return useUrlValue<boolean>(key, fallback, {
    parse: (raw) => raw === '1' || raw === 'true',
    format: (v) => (v ? '1' : '0'),
    isDefault: eq,
  });
}

/** Page number, page size, numeric thresholds. Junk or NaN falls back. */
export function useUrlNumber(key: string, fallback: number): [number, Dispatch<SetStateAction<number>>] {
  return useUrlValue<number>(key, fallback, {
    parse: (raw) => (raw.trim() === '' || Number.isNaN(Number(raw)) ? undefined : Number(raw)),
    format: (v) => String(v),
    isDefault: eq,
  });
}

const EMPTY: string[] = [];

/**
 * Multi-select facet filters (`statusF`, `territoryF`, …), stored comma-separated.
 * The empty list is the default, so an untouched facet adds nothing to the URL.
 */
export function useUrlList(key: string, fallback: string[] = EMPTY): [string[], Dispatch<SetStateAction<string[]>>] {
  return useUrlValue<string[]>(key, fallback, {
    parse: (raw) => (raw === '' ? [] : raw.split(',').filter(Boolean)),
    format: (v) => (v.length ? v.join(',') : null),
    isDefault: (a, b) => a.length === b.length && a.every((x, i) => x === b[i]),
  });
}

export type DateRange = [Dayjs | null, Dayjs | null];
const NO_RANGE: DateRange = [null, null];

/**
 * A date-range filter, stored as `from~to` in `YYYY-MM-DD` (either side may be blank).
 *
 * Dayjs objects cannot round-trip through a URL, so they are re-parsed on read. An unparseable half
 * becomes `null` rather than an Invalid Date, which would otherwise poison every query built from it.
 */
export function useUrlDateRange(key: string, fallback: DateRange = NO_RANGE): [DateRange, Dispatch<SetStateAction<DateRange>>] {
  return useUrlValue<DateRange>(key, fallback, {
    parse: (raw) => {
      const [a, b] = raw.split('~');
      const one = (s?: string) => {
        if (!s) return null;
        const d = dayjs(s, 'YYYY-MM-DD');
        return d.isValid() ? d : null;
      };
      return [one(a), one(b)];
    },
    format: (v) => {
      const [a, b] = v;
      if (!a && !b) return null;
      return `${a ? a.format('YYYY-MM-DD') : ''}~${b ? b.format('YYYY-MM-DD') : ''}`;
    },
    isDefault: (a, b) => {
      const same = (x: Dayjs | null, y: Dayjs | null) => (!x && !y) || (!!x && !!y && x.isSame(y, 'day'));
      return same(a[0], b[0]) && same(a[1], b[1]);
    },
  });
}

/*
 * Three more range shapes, because the pages genuinely use three.
 *
 * `[Dayjs, Dayjs] | null` (null = "no range"), `[Dayjs, Dayjs]` (always has one, defaulted to
 * something like "last 7 days"), and the half-open `[Dayjs | null, Dayjs | null]` above. Wrapping them
 * in one hook would mean casting at ~15 call sites, which is how a type error gets hidden rather than
 * fixed — so each shape gets its own correctly-typed hook over the same `from~to` encoding.
 */
const parseDay = (s?: string): Dayjs | null => {
  if (!s) return null;
  const d = dayjs(s, 'YYYY-MM-DD');
  return d.isValid() ? d : null;
};
const fmtDay = (d: Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '');
const sameDay = (x: Dayjs | null, y: Dayjs | null) => (!x && !y) || (!!x && !!y && x.isSame(y, 'day'));

/** A range that may be absent entirely. Only a complete `from~to` counts — a half range reads as null. */
export function useUrlRangeOrNull(key: string): [[Dayjs, Dayjs] | null, Dispatch<SetStateAction<[Dayjs, Dayjs] | null>>] {
  return useUrlValue<[Dayjs, Dayjs] | null>(key, null, {
    parse: (raw) => {
      const [a, b] = raw.split('~').map(parseDay);
      return a && b ? [a, b] : null;
    },
    format: (v) => (v ? `${fmtDay(v[0])}~${fmtDay(v[1])}` : null),
    isDefault: (a, b) => (a === null && b === null) || (!!a && !!b && sameDay(a[0], b[0]) && sameDay(a[1], b[1])),
  });
}

/**
 * A range that always has a value (its default is usually relative, e.g. the last 7 days).
 *
 * The fallback is rebuilt on every render at the call site, so the parsed value is memoised on the raw
 * query string — otherwise this would hand back a new array each render and churn everything downstream.
 */
export function useUrlRange(key: string, fallback: [Dayjs, Dayjs]): [[Dayjs, Dayjs], Dispatch<SetStateAction<[Dayjs, Dayjs]>>] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const fbKey = `${fmtDay(fallback[0])}~${fmtDay(fallback[1])}`;

  const value = useMemo<[Dayjs, Dayjs]>(() => {
    if (raw) {
      const [a, b] = raw.split('~').map(parseDay);
      if (a && b) return [a, b];
    }
    const [fa, fb] = fbKey.split('~').map(parseDay);
    return [fa!, fb!];
  }, [raw, fbKey]);

  const ref = useRef({ value, fbKey, key });
  ref.current = { value, fbKey, key };

  const set = useCallback<Dispatch<SetStateAction<[Dayjs, Dayjs]>>>(
    (next) => {
      const { value: current, fbKey: fb, key: k } = ref.current;
      const resolved = typeof next === 'function' ? (next as (p: [Dayjs, Dayjs]) => [Dayjs, Dayjs])(current) : next;
      const s = `${fmtDay(resolved[0])}~${fmtDay(resolved[1])}`;
      patchParams(
        (buf) => { if (s === fb) buf.delete(k); else buf.set(k, s); },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams],
  );

  return [value, set];
}

/**
 * A range that is absent until touched, but whose halves may be filled in one at a time.
 *
 * Pages distinguish "no range at all" (`!range` — used for "nothing is filtered" checks and KPI
 * headings) from "a range with one end still blank", so `null` and `[from, null]` must stay different
 * values. Collapsing them would make an untouched list look filtered.
 */
export function useUrlPartialRangeOrNull(
  key: string,
): [[Dayjs | null, Dayjs | null] | null, Dispatch<SetStateAction<[Dayjs | null, Dayjs | null] | null>>] {
  return useUrlValue<[Dayjs | null, Dayjs | null] | null>(key, null, {
    parse: (raw) => {
      const [a, b] = raw.split('~').map(parseDay);
      return a || b ? [a, b] : null;
    },
    format: (v) => (v && (v[0] || v[1]) ? `${fmtDay(v[0])}~${fmtDay(v[1])}` : null),
    isDefault: (a, b) => {
      if (a === null || b === null) return a === b;
      return sameDay(a[0], b[0]) && sameDay(a[1], b[1]);
    },
  });
}

/** A `[min, max]` numeric range (price, land size), stored as `min~max`. */
export function useUrlNumberRange(key: string): [[number, number] | null, Dispatch<SetStateAction<[number, number] | null>>] {
  return useUrlValue<[number, number] | null>(key, null, {
    parse: (raw) => {
      const [a, b] = raw.split('~').map(Number);
      return Number.isFinite(a) && Number.isFinite(b) ? [a, b] : null;
    },
    format: (v) => (v ? `${v[0]}~${v[1]}` : null),
    isDefault: (a, b) => (a === null || b === null ? a === b : a[0] === b[0] && a[1] === b[1]),
  });
}

/** A filter whose "off" value is `null` rather than `undefined` (a selected tile, an open row). */
export function useUrlStringOrNull<T extends string = string>(key: string): [T | null, Dispatch<SetStateAction<T | null>>] {
  return useUrlValue<T | null>(key, null, {
    parse: (raw) => (raw === '' ? null : (raw as T)),
    format: (v) => v,
    isDefault: eq,
  });
}

/** A numeric filter that is `null` when unset. */
export function useUrlNumberOrNull(key: string): [number | null, Dispatch<SetStateAction<number | null>>] {
  return useUrlValue<number | null>(key, null, {
    parse: (raw) => (raw.trim() === '' || Number.isNaN(Number(raw)) ? null : Number(raw)),
    format: (v) => (v == null ? null : String(v)),
    isDefault: eq,
  });
}

/** A numeric filter that is `undefined` when unset. */
export function useUrlNumberOpt(key: string): [number | undefined, Dispatch<SetStateAction<number | undefined>>] {
  return useUrlValue<number | undefined>(key, undefined, {
    parse: (raw) => (raw.trim() === '' || Number.isNaN(Number(raw)) ? undefined : Number(raw)),
    format: (v) => (v == null ? null : String(v)),
    isDefault: eq,
  });
}

/** A single date that always has a value (a week/period cursor). */
export function useUrlDay(key: string, fallback: Dayjs): [Dayjs, Dispatch<SetStateAction<Dayjs>>] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const fbKey = fmtDay(fallback);
  const value = useMemo<Dayjs>(() => parseDay(raw ?? undefined) ?? dayjs(fbKey, 'YYYY-MM-DD'), [raw, fbKey]);

  const ref = useRef({ value, fbKey, key });
  ref.current = { value, fbKey, key };

  const set = useCallback<Dispatch<SetStateAction<Dayjs>>>(
    (next) => {
      const { value: current, fbKey: fb, key: k } = ref.current;
      const resolved = typeof next === 'function' ? (next as (p: Dayjs) => Dayjs)(current) : next;
      const s = fmtDay(resolved);
      patchParams(
        (buf) => { if (s === fb) buf.delete(k); else buf.set(k, s); },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams],
  );

  return [value, set];
}

/** A single date filter. */
export function useUrlDate(key: string): [Dayjs | null, Dispatch<SetStateAction<Dayjs | null>>] {
  return useUrlValue<Dayjs | null>(key, null, {
    parse: (raw) => parseDay(raw),
    format: (v) => (v ? fmtDay(v) : null),
    isDefault: sameDay,
  });
}

/**
 * Clear several filters at once (a "Reset filters" button), in a single history-replacing update.
 *
 * Calling each filter's own setter in a loop works too — the functional update keeps them from
 * clobbering each other — but this states the intent and costs one navigation instead of N.
 */
export function useClearUrlState(): (keys: string[]) => void {
  const [, setParams] = useSearchParams();
  return useCallback(
    (keys: string[]) => {
      patchParams(
        (buf) => { for (const k of keys) buf.delete(k); },
        (final) => setParams(final, { replace: true }),
      );
    },
    [setParams],
  );
}
